// Campaign submissions UI. SubmitPanel is the clipper's half (brief page:
// verify a posting account, submit a post link or a clip made in Clipbait,
// see each clip's views and earnings). ReviewPanel is the campaign owner's
// half (dashboard: accept/deny queue, spend against the pool).
import { useCallback, useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiCheck, FiFilm } from "react-icons/fi";
import { axiosInstance } from "../../config";
import { AuthContext } from "../../context/AuthContext";
import { PLATFORM_META, copyText, fmtMoney, fmtShare, isPool } from "./campaignsShared";

const ACCOUNT_PLATFORMS = [
  ["tiktok", "TikTok"],
  ["instagram", "Instagram"],
  ["facebook", "Facebook"],
  ["youtube", "YouTube"],
];

const STATUS_LABEL = {
  pending: "Checking",
  review: "In review",
  flagged: "In review",
  approved: "Accepted",
  rejected: "Denied",
};
const EARN_LABEL = {
  pending: "pending",
  payable: "ready to pay",
  paid: "paid",
  reversed: "forfeited",
};

export const fmtNum = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(v >= 1e4 ? 0 : 1)}K`;
  return String(v);
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const errMsg = (e, fallback) => e?.response?.data?.error || fallback;

// ── Posting accounts (one line) ─────────────────────────────────────────

function Accounts({ accounts, reload }) {
  const [platform, setPlatform] = useState("tiktok");
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);

  const add = async () => {
    if (!handle.trim()) return;
    setBusy("add");
    try {
      await axiosInstance.post("/clip-submissions/accounts", { platform, handle });
      setHandle("");
      setAdding(false);
      toast("Add the code to your bio, then tap Verify");
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't add that account"));
    } finally {
      setBusy("");
    }
  };
  const verify = async (a) => {
    setBusy(a._id);
    try {
      await axiosInstance.post(`/clip-submissions/accounts/${a._id}/verify`);
      toast.success(`@${a.handle} verified. You can take the code out of your bio now.`);
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't verify yet"));
    } finally {
      setBusy("");
    }
  };
  const remove = async (a) => {
    await axiosInstance.delete(`/clip-submissions/accounts/${a._id}`).catch(() => {});
    reload();
  };

  return (
    <div className="cmp-sp-accounts">
      <span className="cmp-sp-label">Posting as</span>
      {accounts.map((a) => (
        <span key={a._id} className="cmp-sp-account">
          {PLATFORM_META[a.platform]?.icon(14)}@{a.handle}
          {a.verifiedAt ? <span className="cmp-hot" title="Verified"><FiCheck /></span> : (
            <>
              <button type="button" className="cmp-copyable" onClick={() => copyText(a.code, "Code copied, add it to your bio")}>
                {a.code}<span className="cmp-copyable-tag">Copy</span>
              </button>
              <button type="button" className="cmp-sp-link" disabled={busy === a._id} onClick={() => verify(a)}>{busy === a._id ? "Checking…" : "Verify"}</button>
            </>
          )}
          <button type="button" className="cmp-sp-x" aria-label={`Remove @${a.handle}`} onClick={() => remove(a)}>×</button>
        </span>
      ))}
      {adding ? (
        <span className="cmp-sp-account">
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} aria-label="Platform">
            {ACCOUNT_PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input autoFocus placeholder="@handle" value={handle} onChange={(e) => setHandle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <button type="button" className="cmp-sp-link" disabled={busy === "add" || !handle.trim()} onClick={add}>Add</button>
        </span>
      ) : (
        <button type="button" className="cmp-sp-link" onClick={() => setAdding(true)} title="Verify an account once with a code in its bio">
          + {accounts.length ? "account" : "verify your account"}
        </button>
      )}
    </div>
  );
}

// ── Pick a clip made in Clipbait (a strip of thumbnails) ────────────────

const PICKER_MAX = 6; // one row

function ClipbaitPicker({ onPick }) {
  const [clips, setClips] = useState(null);
  useEffect(() => {
    axiosInstance.get("/clip-submissions/clipbait-clips").then((r) => setClips(r.data.clips || [])).catch(() => setClips([]));
  }, []);
  if (clips === null) return <div className="cmp-sp-line">Loading your clips…</div>;
  if (!clips.length) return <div className="cmp-sp-line">No clips yet. Use "Clip with AI" on a stream above.</div>;
  return (
    <div className="cmp-sp-picker">
      {clips.slice(0, PICKER_MAX).map((c) => (
        <button key={`${c.clipJobId}:${c.clipIndex}`} type="button" onClick={() => onPick(c)} title={c.title}>
          {c.thumbnail ? <img src={c.thumbnail} alt="" /> : <video src={c.video} muted preload="metadata" />}
          <span>{c.postUrl ? <>Posted <FiCheck aria-hidden="true" /></> : c.title}</span>
        </button>
      ))}
    </div>
  );
}

// ── One of my submissions: a single status line ─────────────────────────

function MySubmission({ s, usOnly, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("screenshot", file);
    setUploading(true);
    try {
      const r = await axiosInstance.post(`/clip-submissions/${s._id}/us-share`, fd);
      toast.success(`Got it: ${r.data.submission.usShare}% US viewers`);
      onChange();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't read that screenshot"));
    } finally {
      setUploading(false);
    }
  };
  const needsUs = usOnly && s.status === "approved" && s.usShare == null && s.earningsState !== "reversed";
  const bits = [
    s.status === "approved" ? `${fmtNum(s.views)} views` : "",
    usOnly && s.usShare != null ? `${s.usShare}% US` : "",
    s.status === "approved" && !s.trackingDone && s.finalAt ? `keep up till ${fmtDate(s.finalAt)}` : "",
    s.status === "rejected" ? s.reviewNotes : "",
  ].filter(Boolean);
  return (
    <li className={`cmp-sp-clip ${s.status}`}>
      <span className="cmp-sp-clip-icon">{PLATFORM_META[s.platform]?.icon(16)}</span>
      <a href={s.url} target="_blank" rel="noopener noreferrer">{STATUS_LABEL[s.status] || s.status}</a>
      <span className="cmp-sp-clip-meta">{bits.join(" · ")}</span>
      {needsUs && (
        <label className="cmp-sp-link">
          <input type="file" accept="image/*" hidden disabled={uploading} onChange={(e) => upload(e.target.files?.[0])} />
          {uploading ? "Reading…" : "Add US-viewers screenshot"}
        </label>
      )}
      <strong className="cmp-sp-clip-earn">
        {s.poolEstimate != null && s.earned === 0 ? `≈ ${fmtMoney(s.poolEstimate, { cents: true })}` : fmtMoney(s.earned, { cents: true })}
      </strong>
    </li>
  );
}

// ── Clipper panel (brief page, Submit section) ──────────────────────────
// Compact: link + Submit, the accounts line, earnings in one line, then
// the latest few clips (the rest are on the dashboard).

const RECENT_MAX = 4;

export function SubmitPanel({ campaign, onJoined }) {
  const [url, setUrl] = useState("");
  const [picked, setPicked] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [mine, setMine] = useState(null);
  const usOnly = !!campaign.brief?.audience?.usOnly;
  const pooled = isPool(campaign);

  const loadAccounts = useCallback(() => {
    axiosInstance.get("/clip-submissions/accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
  }, []);
  const loadMine = useCallback(() => {
    axiosInstance.get("/clip-submissions/mine", { params: { campaign: campaign._id } })
      .then((r) => setMine(r.data))
      .catch(() => setMine({ submissions: [], totals: {} }));
  }, [campaign._id]);

  useEffect(() => { loadAccounts(); loadMine(); }, [loadAccounts, loadMine]);

  // A clip still being checked: refresh until the AI review lands.
  useEffect(() => {
    if (!mine?.submissions?.some((s) => s.status === "pending")) return undefined;
    const t = setTimeout(loadMine, 8000);
    return () => clearTimeout(t);
  }, [mine, loadMine]);

  // Live pool: shares move as anyone's views update.
  useEffect(() => {
    if (!pooled || campaign.status !== "active") return undefined;
    const t = setInterval(loadMine, 60000);
    return () => clearInterval(t);
  }, [pooled, campaign.status, loadMine]);

  const pick = (c) => {
    setPicked(c);
    if (c.postUrl) setUrl(c.postUrl);
    setShowPicker(false);
  };

  const submit = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await axiosInstance.post("/clip-submissions", {
        campaignId: campaign._id,
        url: url.trim(),
        ...(picked ? { clipJobId: picked.clipJobId, clipIndex: picked.clipIndex } : {}),
      });
      toast.success("Submitted. We're checking it now.");
      setUrl("");
      setPicked(null);
      onJoined?.();
      loadMine();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't submit that clip"));
    } finally {
      setBusy(false);
    }
  };

  const t = mine?.totals || {};
  const subs = mine?.submissions || [];

  return (
    <div className="cmp-sp">
      {campaign.status !== "active" ? (
        <div className="cmp-sp-line"><strong>{pooled ? "This pool has ended." : "Submissions are closed."}</strong> {campaign.budgetClosedAt ? "The pool has been paid out." : ""}</div>
      ) : (
        <>
          <div className="cmp-sp-row">
            <input
              className="cmp-sp-input"
              placeholder="Paste your post link"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <button className="cmp-btn big" disabled={busy || !url.trim()} onClick={submit}>{busy ? "Submitting…" : "Submit"}</button>
          </div>
          <div className="cmp-sp-line">
            {picked ? (
              <>
                <FiFilm aria-hidden="true" /> <strong>{picked.title}</strong>
                {!picked.postUrl && <a className="cmp-sp-link" href={picked.video} target="_blank" rel="noopener noreferrer" download>Download to post</a>}
                <button type="button" className="cmp-sp-link" onClick={() => setPicked(null)}>Clear</button>
              </>
            ) : (
              <button type="button" className="cmp-sp-link" onClick={() => setShowPicker((v) => !v)}><FiFilm aria-hidden="true" /> Made it in Clipbait? Pick the clip</button>
            )}
          </div>
          {showPicker && <ClipbaitPicker onPick={pick} />}
          <Accounts accounts={accounts} reload={loadAccounts} />
        </>
      )}

      {subs.length > 0 && (
        <>
          {pooled && <PoolShare p={mine?.pools?.[campaign._id]} />}
          <div className="cmp-sp-earn">
            <span><strong>{fmtMoney(t.pending, { cents: true })}</strong> pending</span>
            <span><strong>{fmtMoney(t.payable, { cents: true })}</strong> ready</span>
            <span><strong>{fmtMoney(t.paid, { cents: true })}</strong> paid</span>
            <span><strong>{fmtNum(t.views)}</strong> views</span>
          </div>
          <ul className="cmp-sp-clips">
            {mine?.feeRate > 0 && <li className="cmp-pool-note">Shown after the {Math.round(mine.feeRate * 100)}% platform fee.</li>}
            {subs.slice(0, RECENT_MAX).map((s) => <MySubmission key={s._id} s={s} usOnly={usOnly} onChange={loadMine} />)}
          </ul>
          {subs.length > RECENT_MAX && (
            <a className="cmp-sp-link" href="/campaigns/dashboard">All {subs.length} clips on your dashboard →</a>
          )}
        </>
      )}
    </div>
  );
}

// A clipper's live cut of a pool. An estimate until the pool locks.
function PoolShare({ p }) {
  if (!p) return null;
  const locked = p.state === "locked";
  return (
    <div className="cmp-sp-earn">
      <span><strong>{fmtShare(p.myShare)}</strong> of the pool</span>
      <span><strong>{locked ? "" : "≈ "}{fmtMoney(p.myEstimate, { cents: true })}</strong> {locked ? "final" : "estimated"}</span>
      <span><strong>{fmtNum(p.myViews)}</strong> of {fmtNum(p.totalViews)} views</span>
      <span className="cmp-pool-note">
        {p.state === "open" && `Moves as others post. Ends ${fmtDate(p.endsAt)}.`}
        {p.state === "reviewing" && "Pool closed. Final clips being reviewed."}
        {locked && (p.holdUntil && new Date(p.holdUntil) > new Date() ? `Locked. Released ${fmtDate(p.holdUntil)}.` : "Locked and released.")}
      </span>
    </div>
  );
}

// ── Clipper earnings across campaigns (dashboard) ───────────────────────

export function EarningsSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axiosInstance.get("/clip-submissions/mine").then((r) => setData(r.data)).catch(() => setData({ submissions: [], totals: {} }));
  }, []);
  if (!data || !data.submissions.length) return null;
  const t = data.totals;
  return (
    <>
      <h2 className="cmp-dash-h">Your earnings</h2>
      <div className="cmp-earn-strip">
        <div><strong>{fmtMoney(t.pending, { cents: true })}</strong><span>pending</span></div>
        <div><strong>{fmtMoney(t.payable, { cents: true })}</strong><span>ready to pay</span></div>
        <div><strong>{fmtMoney(t.paid, { cents: true })}</strong><span>paid</span></div>
        {t.estimated > 0
          ? <div><strong>≈ {fmtMoney(t.estimated, { cents: true })}</strong><span>pool estimate</span></div>
          : <div><strong>{t.clips}</strong><span>clips submitted</span></div>}
      </div>
      {data.feeRate > 0 && <p className="cmp-pool-note">Shown after the {Math.round(data.feeRate * 100)}% platform fee.</p>}
      <p className="cmp-muted small">Pending earnings become payable once a clip's keep-up window ends with the post still live. Payouts are sent weekly.</p>
    </>
  );
}

// ── Owner panel (dashboard) ─────────────────────────────────────────────

const QUEUE = ["review", "flagged"];

export function ReviewPanel({ campaign }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("queue");
  const [busy, setBusy] = useState("");
  const usOnly = !!campaign.brief?.audience?.usOnly;
  const { user } = useContext(AuthContext);

  const load = useCallback(() => {
    axiosInstance.get(`/clip-submissions/campaign/${campaign._id}`).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load submissions"));
  }, [campaign._id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (s, decision) => {
    let note = "";
    if (decision === "deny") {
      note = window.prompt("Reason (the clipper sees this):", "Doesn't follow the brief");
      if (note === null) return;
    }
    setBusy(s._id);
    try {
      await axiosInstance.post(`/clip-submissions/${s._id}/decision`, { decision, note });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save"));
    } finally {
      setBusy("");
    }
  };
  const setUs = async (s) => {
    const v = window.prompt("US share of views (0-100):", s.usShare?.pct ?? "");
    if (v === null || v === "") return;
    try {
      await axiosInstance.post(`/clip-submissions/${s._id}/us-share`, { pct: Number(v) });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save"));
    }
  };

  const endPool = async () => {
    if (!window.confirm("End this pool now? No new clips, and payouts lock after the final review.")) return;
    try {
      await axiosInstance.post(`/campaigns/${campaign._id}/close-pool`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't end the pool"));
    }
  };

  if (!data) return <div className="cmp-muted small">Loading submissions…</div>;
  const { submissions, spend, counts, pool } = data;
  const locked = pool?.state === "locked";
  const waits = pool?.blockers ? [
    pool.blockers.toReview ? `${pool.blockers.toReview} to review` : "",
    pool.blockers.toCheck ? `${pool.blockers.toCheck} final view checks` : "",
    pool.blockers.noUsShare ? `${pool.blockers.noUsShare} waiting on US %` : "",
  ].filter(Boolean) : [];
  const rows = submissions.filter((s) => (tab === "queue" ? QUEUE.includes(s.status) || s.status === "pending" : tab === "accepted" ? s.status === "approved" : s.status === "rejected"));
  const queueN = (counts.review || 0) + (counts.flagged || 0) + (counts.pending || 0);

  return (
    <div className="cmp-review">
      {pool ? (
        <>
          <div className="cmp-earn-strip">
            <div><strong>{fmtMoney(pool.pool)}</strong><span>pool</span></div>
            <div><strong>{fmtNum(pool.totalViews)}</strong><span>counted views</span></div>
            <div><strong>{fmtMoney(pool.effectiveCpm, { cents: true })}</strong><span>per 1K views now</span></div>
            <div><strong>{pool.clippers}</strong><span>clippers</span></div>
          </div>
          <div className="cmp-pool-status">
            {pool.state === "open" && <span>Ends {fmtDate(pool.endsAt)}</span>}
            {pool.state === "reviewing" && <span>Closing{waits.length ? `: ${waits.join(" · ")}` : ": locking payouts within the hour"}</span>}
            {locked && <span>Locked: {fmtMoney(spend.reserved, { cents: true })} to clippers. Released {fmtDate(pool.holdUntil)}.</span>}
            {pool.state === "open" && user?.isAdmin && <button className="cmp-btn sm ghost" onClick={endPool}>End pool now</button>}
          </div>
        </>
      ) : (
        <div className="cmp-earn-strip">
          <div><strong>{fmtMoney(spend.reserved, { cents: true })}</strong><span>earned by clippers</span></div>
          <div><strong>{fmtMoney(spend.left, { cents: true })}</strong><span>left in pool</span></div>
          <div><strong>{fmtNum(spend.views)}</strong><span>views</span></div>
          <div><strong>{spend.clippers}</strong><span>clippers</span></div>
        </div>
      )}
      <div className="cmp-review-tabs">
        <button className={tab === "queue" ? "on" : ""} onClick={() => setTab("queue")}>To review ({queueN})</button>
        <button className={tab === "accepted" ? "on" : ""} onClick={() => setTab("accepted")}>Accepted ({counts.approved || 0})</button>
        <button className={tab === "denied" ? "on" : ""} onClick={() => setTab("denied")}>Denied ({counts.rejected || 0})</button>
      </div>
      {rows.length === 0 ? <div className="cmp-muted small">Nothing here.</div> : (
        <div className="cmp-sub-list">
          {rows.map((s) => {
            const views = Math.max(s.tracking?.views || 0, s.scrape?.viewCount || 0);
            const issues = [
              ...(s.compliance?.notes && s.status === "flagged" ? [s.compliance.notes] : []),
              ...(s.fraudSignal?.reasons || []),
              ...(s.reviewError ? ["Couldn't check it automatically"] : []),
            ];
            return (
              <div key={s._id} className="cmp-sub-row">
                <span className="cmp-account-icon">{PLATFORM_META[s.platform]?.icon(16)}</span>
                <div className="cmp-sub-main">
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="cmp-sub-url">
                    {s.scrape?.username ? `@${s.scrape.username}` : s.url.replace(/^https?:\/\/(www\.)?/, "")}
                  </a>
                  <span className="cmp-sub-by"> by {s.user?.username || "clipper"}</span>
                  <div className="cmp-sub-meta">
                    {s.status === "pending" && <span className="cmp-pill">Checking</span>}
                    {s.status === "review" && <span className="cmp-pill active">AI check passed</span>}
                    {s.status === "flagged" && <span className="cmp-pill flagged">Flagged</span>}
                    <span>{fmtNum(views)} views</span>
                    <span className={s.accountVerified ? "ok" : "warn"}>{s.accountVerified ? "Verified account" : "Account not verified"}</span>
                    {s.source?.kind === "clipbait" && <span>Made in Clipbait</span>}
                    {s.scrape?.postedAt && <span>posted {fmtDate(s.scrape.postedAt)}</span>}
                    {usOnly && s.status === "approved" && (
                      <button className="cmp-link" onClick={() => setUs(s)}>{s.usShare?.pct != null ? `${s.usShare.pct}% US` : "Set US %"}</button>
                    )}
                    {s.usShare?.screenshotUrl && <a href={s.usShare.screenshotUrl} target="_blank" rel="noopener noreferrer">analytics</a>}
                  </div>
                  {issues.length > 0 && <div className="cmp-sub-issues">{issues.join(" · ")}</div>}
                  {s.status === "rejected" && s.reviewNotes && <div className="cmp-sub-issues">{s.reviewNotes}</div>}
                </div>
                <div className="cmp-sub-earn">
                  {pool && !locked ? (
                    <>
                      <strong>{s.poolEstimate != null ? `≈ ${fmtMoney(s.poolEstimate, { cents: true })}` : "—"}</strong>
                      <span>{s.poolShare != null ? fmtShare(s.poolShare) : ""}</span>
                    </>
                  ) : (
                    <>
                      <strong>{fmtMoney(s.earnings?.accrued, { cents: true })}</strong>
                      <span>{EARN_LABEL[s.earnings?.state] || ""}</span>
                    </>
                  )}
                </div>
                <div className="cmp-sub-actions">
                  {!locked && s.status !== "approved" && s.status !== "pending" && (
                    <button className="cmp-btn sm" disabled={busy === s._id} onClick={() => decide(s, "approve")}>Accept</button>
                  )}
                  {!locked && s.status !== "rejected" && s.status !== "pending" && s.earnings?.state !== "paid" && (
                    <button className="cmp-btn sm ghost" disabled={busy === s._id} onClick={() => decide(s, "deny")}>{s.status === "approved" ? "Remove" : "Deny"}</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
